import React, { useEffect, useState } from 'react';
import Title from '../../../components/common/Title';
import { Topbar } from '../../../components/include/Topbar';
import Pagination from '../../../components/common/Pagination';
import { useNavigate } from 'react-router-dom';
import { Postdata } from '../../../helpers/api.service';
import HttpService from '../../../helpers/httpServices';
import { message } from '../../../redux/actions/toastActions';
import { useDispatch, useSelector } from 'react-redux';
import DeleteModal from '../../../components/common/DeleteModal';
import Error403Page from '../../ErrorPage';
import { isPermission, isRole, isRoleType } from '../../../redux/selectors/authSelector';
import { useCountryNavigate } from '../../../components/common/UseCountryNavigate';

const ContentMain = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpenDelete, setModalOpenDelete] = useState(false);
  const [loading, setLoading] = useState(false);  // Unified loader state
  const [isDeleting, setIsDeleting] = useState(false);  // Loader for delete action
  const [isToggling, setIsToggling] = useState(false);  // Loader for status toggle action
  // const navigate = useNavigate();
  const navigate = useCountryNavigate();
  const dispatch = useDispatch();

  const permissions = useSelector(isPermission);
  const role = useSelector(isRole);
  const role_type = useSelector(isRoleType)

  const isAccessible = role === 'admin' || role === 'subadmin' || role_type === "schoolsubadmin" || role_type === "subadmin" || permissions?.includes('view_Content');
  const isEditable = role === 'admin' || role === 'subadmin' || role_type === "schoolsubadmin" || role_type === "subadmin" || permissions?.includes('edit_Content');
  const isDeletable = role === 'admin' || role === 'subadmin' || role_type === "schoolsubadmin" || role_type === "subadmin" || permissions?.includes('delete_Content');

  const fetchData = (page = currentPage, rows = perPage) => {
    setLoading(true);
    Postdata(HttpService.admin_content_list, { page, perpage: rows })
      .then((res) => {
        if (res.status === 200) {
          setData(res.data.pages);
          setTotalRows(res.data.count);
        }
        // else {
        //   dispatch(message(res.message));
        // }
      })
      .finally(() => setLoading(false));
  };

  const handleDelete = (id) => {
    setIsDeleting(true);
    Postdata(HttpService.admin_content_delete, { id })
      .then((res) => {
        // dispatch(message(res.message));
        if (res.status === 200) fetchData();
      })
      .finally(() => setIsDeleting(false));
  };

  const handleStatusToggle = (id) => {
    setIsToggling(true);
    Postdata(HttpService.admin_content_status, { id })
      .then((res) => {
        // dispatch(message(res.message));
        if (res.status === 200) fetchData();
      })
      .finally(() => setIsToggling(false));
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, perPage]);

  if (!isAccessible) {
    return <Error403Page />;
  }

  return (
    <>
      <Title name="Content Management List" />
      <Topbar title="Content Management List" />
      <div className="p-6 space-y-6 pr-0">
        <div className="bg-white rounded overflow-x-auto">
          <table className="min-w-full text-sm text-left">
            <thead className="text-gray-700">
              <tr>
                <th className="px-6 py-3 font-medium">Title</th>
                <th className="px-6 py-3 font-medium">Type</th>
                <th className="px-6 py-3 font-medium">Status</th>
                {(isEditable || isDeletable) && (
                  <th className="px-6 py-3 font-medium">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center py-4">
                    <div className="loader">Loading...</div>
                  </td>
                </tr>
              ) : (
                data?.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">{item.title}</td>
                    <td className="px-6 py-4">{item.type}</td>
                    <td className="px-6 py-4">
                      {isEditable ? (
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={item.status === 1}
                            onChange={() => handleStatusToggle(item._id)}
                            disabled={isToggling}  // Disable while toggling
                          />
                          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary_main rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary_main"></div>
                        </label>
                      ) : item.status === 1 ? "Active" : "Inactive"}
                    </td>
                    {(isEditable || isDeletable) && (
                      <td className="px-6 py-4 space-x-3">
                        {isEditable && (
                          <button
                            className="text-primary_main hover:underline"
                            onClick={() =>
                              navigate(`admin-content/edit/${item._id}`, {
                                state: { type: "edit" },
                              })
                            }
                          >
                            Edit
                          </button>
                        )}
                        {isDeletable && (
                          <button
                            className="text-red-600 hover:underline"
                            onClick={() => {
                              setSelectedId(item._id);
                              setModalOpenDelete(true);
                            }}
                            disabled={isDeleting}  // Disable while deleting
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className='m-4'>
            <Pagination
              currentPage={currentPage}
              totalRows={totalRows}
              perPage={perPage}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={(rows) => {
                setPerPage(rows);
                setCurrentPage(1);
              }}
            />
          </div>
          {data.length === 0 && !loading && (
            <div className="h-[50vh] flex items-center justify-center w-full">
              <h1 className="text-center text-gray-400">No Data Found</h1>
            </div>
          )}
        </div>
        <DeleteModal
          title="Delete Page"
          message="Are you sure you want to delete Page? It will erase all the data permanently."
          modalOpenDelete={modalOpenDelete}
          setModalOpenDelete={setModalOpenDelete}
          handleDelete={handleDelete}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
        />
      </div>
    </>
  );
};

export default ContentMain;
